/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import {
  PopularSearchNewsListWrap,
  PopularSearchTermListWrap,
  PopularSearchTermPopupWrap,
} from '@components/PopularSearchTerm/index.style'
import { ListView } from 'antd-mobile'
import _ from 'lodash'
import { AdvEmpty } from '@components/AdvEmpty'
import { EMPTY_ICON } from '@constants/index'
import { Button } from '@styles/button.style'
import PopupUtils from '@utils/popup-utils'
import { isMobile } from 'react-device-detect'
import { XEmbed, YouTubeEmbed } from 'react-social-media-embed'
import { formatCreatedAtToElapsedTime } from '@utils/utils'
import { useOnMount } from '@utils/reactExt'
import useRootStore from '@store/useRootStore'
import { Schema$PopularSearchTerm, Schema$PopularSearchTermNews } from '@protocol/popularSearchTerm'
import StorageUtils from '@utils/storage-utils'
import dayjs from 'dayjs'
//img
import popularSearchNowTxtImg from '@assets/images/text/popular-search-now-txt.png'
import popularSearchNewsTxtImg from '@assets/images/text/popular-search-news-txt.png'
import { POPULAR_SEARCH_TERM_OPTION_KEY } from '@v2/constants/popular-search-term'

export interface PopularSearchTermPopupProps {
  popularSearchTerm: Schema$PopularSearchTerm
}

const PopularSearchTermPopup: React.FC<PopularSearchTermPopupProps> = observer((props) => {
  const { popularSearchTerm } = props
  const { term, xUrl, youtubeUrl, order } = popularSearchTerm
  const { popularSearchTermStore, globalStore, logStore } = useRootStore()
  const { popularSearchTermNews, popularSearchTerms } = popularSearchTermStore
  const [readUrls, setReadUrls] = useState<string[]>([])

  const options = globalStore.getPopularSearchTermOptions()
  const newsReadHistoryLifeTime = options[POPULAR_SEARCH_TERM_OPTION_KEY.NEWS_READ_HISTORY_LIFE_TIME]

  useOnMount(() => {
    initReadUrls()
    popularSearchTermStore.fetchPopularSearchTermNews({
      termId: popularSearchTerm.id,
      reset: true,
    }).catch(console.error)
  })

  useEffect(() => {
    // 일자를 유지해야 한다.
    return () => {
      const map: { [key: string]: Date } = {}
      _.forEach(readUrls, url => {
        map[url] = new Date()
      })
      StorageUtils.savePopularSearchTermNewsReadUrlMap(map)
    }
  }, [readUrls])

  const initReadUrls = (): void => {
    const urlMap = StorageUtils.getPopularSearchTermNewsReadUrlMap<{ [key: string]: Date }>()
    const lifeTime = dayjs().add(newsReadHistoryLifeTime * -1, 'days').toDate()
    const filteredLinks = _.pickBy(urlMap, (date) => new Date(date) > lifeTime)
    setReadUrls(_.keys(filteredLinks))
  }

  const dataSource = new ListView.DataSource({
    rowHasChanged: (row1: any, row2: any) => row1 !== row2,
  })

  const visibleIssueKeyword = _.findIndex(popularSearchTerms, { id: popularSearchTerm.id}) > -1

  const renderHeader = (): React.ReactElement => {
    const keywordNumber = _.findIndex(popularSearchTerms, { order }) + 1
    return (
      <PopularSearchTermListWrap className={!isMobile && 'pc'}>
        <div className="popular-keyword">
          {visibleIssueKeyword && (
            <div className="popular-keyword--txt">
              <img src={popularSearchNowTxtImg} alt="이슈 키워드 now" loading="lazy" className="txt-img"/>
              <span className="keyword-num">{keywordNumber}</span>
            </div>
          )}
          <p className="popular-keyword--contents">{term}</p>
        </div>
        {!_.isEmpty(xUrl) &&
          <Fragment>
            <p className="list-title">트위터</p>
            <XEmbed url={xUrl} width="100%"/>
          </Fragment>
        }
        {!_.isEmpty(youtubeUrl) &&
          <Fragment>
            <p className="list-title">영상</p>
            <YouTubeEmbed url={youtubeUrl} width="100%"/>
          </Fragment>
        }
        {!_.isEmpty(popularSearchTermNews) &&
          <p className="list-title">
            <img src={popularSearchNewsTxtImg} alt="이 시각 추천 뉴스" loading="lazy" className="news-txt"/>
          </p>
        }
      </PopularSearchTermListWrap>
    )
  }

  const renderRow = (newsItem: Schema$PopularSearchTermNews) => {
    const { url } = newsItem
    const onClick = (url: string) => {
      logStore.writeButtonLog({ pageId: 'j010', buttonId: '002' }).catch(console.error)
      PopupUtils.showNewsPopup(url)

      setReadUrls((prevReadUrls) =>
        prevReadUrls.includes(url)
          ? prevReadUrls
          : [...prevReadUrls, url],
      )
    }
    return (
      <PopularSearchNewsListWrap>
        <Button
          TP
          onClick={() => onClick(newsItem.url)}
          className={`popular-search-news-btn ${readUrls.includes(url) && 'clicked'}`}
        >
          <div className="title-box">
            <p className="title">{newsItem.title}</p>
            <span className="date">
              {formatCreatedAtToElapsedTime({ createdAt: newsItem.pubDate })}
            </span>
          </div>
        </Button>
      </PopularSearchNewsListWrap>
    )
  }

  const endReachedHandler = async () => {
    if (popularSearchTermStore.popularSearchTermNewsHasMore) {
      await popularSearchTermStore.fetchPopularSearchTermNews({ termId: popularSearchTerm.id })
    }
  }

  const renderFooter = () => {
    let footer
    if (_.size(popularSearchTermNews) === 0 && _.isEmpty(xUrl) && _.isEmpty(youtubeUrl)) {
      footer = <AdvEmpty ico={EMPTY_ICON.POPULAR_SEARCH_LIST}/>
    } else {
      footer = <Fragment/>
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  return (
    <PopularSearchTermPopupWrap>
      <ListView
        dataSource={dataSource.cloneWithRows(popularSearchTermNews || [])}
        renderHeader={renderHeader}
        renderRow={renderRow}
        renderFooter={renderFooter}
        onEndReached={endReachedHandler}
        scrollRenderAheadDistance={500}
        onEndReachedThreshold={10}
        pageSize={10}
        initialListSize={10}
        css={{ height: '100%' }}
      />
    </PopularSearchTermPopupWrap>
  )
})

export default PopularSearchTermPopup
